<template>
    <v-layout row>
        <v-flex xs12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-toolbar>
            </v-card>
            <v-progress-linear indeterminate  v-if="loading" height="2" class="mt-0"></v-progress-linear>
            
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                {{errorMessage}}
            </v-alert>  

            <!-- APPEARANCE -->
            <v-card class="elevation-1 mt-5">
                <v-toolbar @click="appearanceVisible=!appearanceVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">APPEARANCE</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="appearanceVisible" icon title="Minimize">
                        <v-icon>expand_less</v-icon>
                    </v-btn>
                    <v-btn v-if="!appearanceVisible" icon title="Expand">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-layout v-if="appearanceVisible" row wrap :class="isMobile?'ml-20 mr-20':'ml-25 mr-20'">
                    <p class="mt-15 mb-5">Choose your preferred look. Click a theme color button to set it as your primary color and header image. Use the display mode buttons (light or dark) to switch instantly.</p>
                    <v-flex xs12>
                        <div class="font-weight-bold">Theme Color</div>         
                        <color-settings @send="updateHeaderImage"></color-settings>          
                    </v-flex>
                    <v-flex xs12 class="mt-10 mb-15">
                        <div class="font-weight-bold">Display Mode</div>         
                        <v-btn-toggle v-model="toggle_theme" dark>
                            <v-btn color="black" class="no-text-btn" @click="updateTheme('dark')"></v-btn>
                            <v-btn color="white" class="no-text-btn" @click="updateTheme('light')"></v-btn>
                        </v-btn-toggle>           
                    </v-flex>
                </v-layout>
            </v-card>
     
            <!-- EMAIL & PASSWORD LOGIN -->
            <v-card class="elevation-1 mt-5">
                <v-toolbar @click="securityVisible=!securityVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">EMAIL & PASSWORD LOGIN</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="securityVisible" icon title="Minimize">
                        <v-icon>expand_less</v-icon>
                    </v-btn>
                    <v-btn v-if="!securityVisible" icon title="Expand">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-layout v-if="securityVisible" row wrap :class="isMobile?'ml-20 mr-20 mt-15':'ml-25 mr-20 mt-15'">
                    <v-flex>
                        <div class="font-weight-bold">Enable OTP for Email + Password Login</div>
                        <v-switch
                            v-model="twoFA"
                            label="Enable 2FA with OTP"
                            color="primary"
                            hide-details
                            class="mt-0"
                            @change="updateTwoFA"
                        ></v-switch>   
                        <div class="caption mt-10 mb-10">Adds a one‑time password (OTP) when signing in with email and password. Recommended for stronger security.</div>
                    </v-flex>
                    <v-flex xs12 class="mb-10">
                        <div class="font-weight-bold">Password</div>
                        <v-btn small class="primary ml-0" :loading="loading" to="/changepassword">Change Password</v-btn>
                    </v-flex>
                </v-layout>
            </v-card>

            <!-- TRUSTED DEVICES -->
            <v-card class="elevation-1 mt-5">
                <v-toolbar @click="trustedDevicesVisible=!trustedDevicesVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">TRUSTED DEVICES</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="trustedDevicesVisible" icon title="Minimize">
                        <v-icon>expand_less</v-icon>
                    </v-btn>
                    <v-btn v-if="!trustedDevicesVisible" icon title="Expand">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </v-toolbar>
                <p v-if="trustedDevicesVisible" :class="isMobile?'ml-20 mt-15 mb-5 mr-20':'ml-25 mt-15 mb-5 mr-20'">
                    Manage the devices you use to access SweldoNow. Enabling <b>Device Login</b> 
                    allows you to sign in faster using your biometrics or PIN—eliminating 
                    the need for passwords and OTPs.
                </p>  
                <v-data-table
                    v-if="trustedDevicesVisible"
                    :headers="isMobile ? [] : trusted_header"
                    :items="trusted_devices"
                    :hide-actions="trusted_devices.length <= 5"
                    :hide-headers="isMobile"
                >
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="trusted_header.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-toolbar flat height="42">
                                        <div class="font-weight-bold">
                                            {{ props.item.deviceName }}
                                            <span v-if="isThisDevice(props.item)" class="primary--text"> (this device)</span>
                                        </div>
                                        <v-spacer></v-spacer>
                                        <div>{{ props.item.deviceType }}</div>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5">
                                            <v-layout row>
                                                <v-flex xs6>
                                                    <div class="caption grey--text text--darken-1">Vendor & Model</div>
                                                    <span v-if="props.item.deviceModel !== 'Unknown'">
                                                        {{ props.item.deviceVendor }} {{ props.item.deviceModel }}
                                                    </span>
                                                    <span v-else class="grey--text italic">Generic {{ props.item.deviceType }}</span>
                                                </v-flex>
                                                <v-flex xs6>
                                                    <div class="caption grey--text text--darken-1">OS & Version</div>
                                                    <div>{{ props.item.osName }} {{ props.item.osVersion }}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                        <v-flex xs12 class="text-xs-left mt-5">
                                            <v-layout row>
                                                <v-flex xs6>
                                                    <div class="caption grey--text text--darken-1">Last Seen</div>
                                                    <div>{{ props.item.lastSeen | moment('MMM DD, YYYY h:mma') }}</div>
                                                </v-flex>
                                                <v-flex xs6>
                                                    <div class="caption grey--text text--darken-1">Device Login</div>
                                                    <div>
                                                        <div v-if="isThisDevice(props.item)">
                                                            <span v-if="props.item.isDeviceLoginEnabled" class="green--text">Enabled</span>
                                                            <v-btn v-if="canEnableThisDevice(props.item)" @click="dialogEnableDeviceLogin=true" small class="primary ml-0" title="Click to enable device login">
                                                                Enable
                                                            </v-btn>
                                                        </div>
                                                        <div v-else>
                                                            <span v-if="props.item.isDeviceLoginEnabled" class="green--text">Enabled</span>
                                                            <span v-else>Not enabled</span>
                                                        </div>                
                                                    </div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                    <v-divider></v-divider>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <v-btn @click="showRemoveConfirmation(props.item)" icon small title="Remove this device" class="ml-0">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else>
                            <td class="font-weight-bold">
                                {{ props.item.deviceName }}
                                <span v-if="isThisDevice(props.item)" class="primary--text"> (this device)</span>
                            </td>
                            <td>{{ props.item.deviceType }}</td>
                            <td>
                                <span v-if="props.item.deviceModel !== 'Unknown'">
                                    {{ props.item.deviceVendor }} {{ props.item.deviceModel }}
                                </span>
                                <span v-else class="grey--text italic">
                                    {{ props.item.browserName }} on {{ props.item.osName }}
                                    <!-- Generic {{ props.item.deviceType }} -->
                                </span>
                            </td>
                            <td>{{ props.item.osName }} {{ props.item.osVersion }}</td>
                            <td>{{ props.item.lastSeen | moment('MMM DD, YYYY h:mma') }}</td>
                            <td>
                                <div v-if="isThisDevice(props.item)">
                                    <span v-if="props.item.isDeviceLoginEnabled" class="green--text">Enabled</span>
                                    <v-btn v-if="canEnableThisDevice(props.item)" @click="dialogEnableDeviceLogin=true" small class="primary ml-0" title="Click to enable device login">
                                        Enable
                                    </v-btn>
                                </div>
                                <div v-else>
                                    <span v-if="props.item.isDeviceLoginEnabled" class="green--text">Enabled</span>
                                    <span v-else>Not enabled</span>
                                </div>                       
                            </td>
                            <td>
                                <v-btn @click="showRemoveConfirmation(props.item)" icon small title="Remove this device" class="ml-0">
                                    <v-icon small>delete</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>

            <!-- NOTIFICATIONS -->
            <!-- future improvements: notifications by app, to allow notif config per app -->
            <v-card class="elevation-1 mt-5">
                <v-toolbar @click="notificationsVisible=!notificationsVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">NOTIFICATIONS</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="notificationsVisible" icon title="Minimize">
                        <v-icon>expand_less</v-icon>
                    </v-btn>
                    <v-btn v-if="!notificationsVisible" icon title="Expand">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-layout v-if="notificationsVisible" wrap row :class="isMobile?'ml-20 mt-15 mr-20':'ml-25 mt-15 mr-20'">
                    <p class="mb-5">Manage how you receive updates for HR and payroll activities.</p>
                    <v-flex xs12 class="mt-0 mb-10">
                        <v-switch
                            v-model="appNotification"
                            label="App Notifications"
                            color="primary"
                            hide-details
                            class="mt-0"
                            @change="updateNotifications"
                        ></v-switch>   
                        <v-switch
                            v-model="emailNotification"
                            label="Email Notifications"
                            color="primary"
                            hide-details
                            @change="updateNotifications"
                        ></v-switch>  
                    </v-flex>
                    <v-flex xs12>
                        <div class="font-weight-bold caption">Note (always visible, non-editable):</div>
                        <div class="caption">
                            Notifications for Payslips, New Employee Creation, OTP, Password Reset, and New Device Login are always enabled.
                        </div>
                    </v-flex>
                    <v-flex xs12>
                        <div class="font-weight-bold caption">Features covered by switches:</div>
                        <div class="mb-10 caption">
                            Attendance Corrections, Time Off, Overtime, Timesheet, Workshift Planning, Compensation & Benefits, Payroll, Finance
                        </div>
                    </v-flex>
                </v-layout>
            </v-card>
        </v-flex>

        <v-dialog v-model="dialogOkCancel" persistent width="550">
            <ok-cancel :msg="dialogOkCancelMsg" alignLeft="true" :dialogOkCancel.sync="dialogOkCancel" @ok="removeDeviceLogin"></ok-cancel>
        </v-dialog>
        <v-dialog v-model="dialogEnableDeviceLogin" width="600" persistent>   
            <enable-device-login @success="onEnableDeviceSuccess" @fail="onEnableDeviceFailure" :dialogEnableDeviceLogin.sync="dialogEnableDeviceLogin"></enable-device-login>
        </v-dialog>
        <v-dialog v-model="dialogOkOnly" width="500" persistent>
            <ok-only :title="dialogOkOnlyTitle" :msg="dialogOkOnlyMsg" :dialogOkOnly.sync="dialogOkOnly"></ok-only>
        </v-dialog>

    </v-layout>  
</template>
<script>

import userApi from '@/api/user.js'
import userDevicesApi from '@/api/userdevices'
import { isPlatformAuthenticatorAvailable } from '@/helpers/webauthn'
import { getUAData } from '@/helpers/user-agent.js'
import { clearDeviceLoginCooldowns, getExpandCollapseVisibility, updateExpandCollapseVisibility } from '@/helpers/user-prefs'

export default {  
    name: 'Settings',
    metaInfo: {
        title: 'My Settings',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Change page primary color, change dark or light theme.' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'color-settings': () => import('@/components/Settings/Colors'),
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
        'ok-only': () => import('@/components/BasicPopups/OkOnly'),
        'enable-device-login': () => import('@/components/DeviceLogin/EnableDeviceLogin'),
    },      
    data () {
        const routeName = this.$route.name

        return {
            //for refactor - create reusable rules-helpers.js
            rules: {
                required: value => !!value || "Required.",
                min: v => v.length >= 8 || "Min 8 characters",
                counter: value => value.length <= 20 || "Max 20 characters",
                email: value => {
                const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return pattern.test(value) || "Invalid e-mail.";
                }
            },
            trusted_header: [
                {text: 'Device Name',align: 'left',value: '',width: '20%', sortable:false},
                {text: 'Type',align: 'left',value: '',width: '10%', sortable:false},
                {text: 'Vendor & Model',align: 'left',value: '',width: '15%', sortable:false},
                {text: 'OS & Version',align: 'left',value: '',width: '12%', sortable:false},
                {text: 'Last Seen',align: 'left',value: '',width: '15%', sortable:false},
                {text: 'Device Login',align: 'left',value: '',width: '10%', sortable:false},
                {text: 'Remove',align: 'left',value: '',width: '10%', sortable:false},
            ],
            appearanceVisible: getExpandCollapseVisibility(routeName, 'mysettings-appearance'),
            securityVisible: getExpandCollapseVisibility(routeName, 'mysettings-security'),
            trustedDevicesVisible: getExpandCollapseVisibility(routeName, 'mysettings-trusted-devices'),
            notificationsVisible: getExpandCollapseVisibility(routeName, 'mysettings-notifications'),
            loading: false,
            toggle_colors: null,
            toggle_theme: null,
            darkTheme: false,
            error: false,
            twoFA: null,
            appNotification: null,
            emailNotification: null,
            errorMessage: '', 
            headerImage: '',
            default_theme: 'light',
            deviceLoginSupported : false,
            trusted_devices: [],
            userAgent: null,
            selectedUserDeviceData: null,
            dialogOkCancel: false,
            dialogOkCancelMsg: '',
            dialogEnableDeviceLogin: false,
            dialogOkOnly: false,
            dialogOkOnlyTitle: null,
            dialogOkOnlyMsg: null,
        }
    },
    methods: {
        async onEnableDeviceSuccess(deviceName) {
            // 1. Close the registration modal
            this.dialogEnableDeviceLogin = false

             // 2. Show the success notification
            this.dialogOkOnlyTitle = "Success! This device is now trusted."
            this.dialogOkOnlyMsg = `Your <b>${deviceName || 'device'}</b> is now set up for biometric login. ${this.isMobile ? '': '<br>'}You can now sign in faster without using a password.`
            this.dialogOkOnly = true

            // 3. Update the state so the popup doesn't show again in this session
            this.$store.commit("setIsDeviceLoginCredentialExists", true)

            //Clear local cooldowns since they successfully registered
            clearDeviceLoginCooldowns()

            //refresh trusted devices list
            this.getTrustedDevices()
        },
        onEnableDeviceFailure() {
             //Close the registration modal
            this.dialogEnableDeviceLogin = false

            //Show the failure notification
            this.dialogOkOnlyTitle = "Registration Unsuccessful"
            this.dialogOkOnlyMsg = "We couldn't link your device at this time. This can happen if the biometric scan was interrupted or timed out. You can try again later in My Settings."
            this.dialogOkOnly = true

            //refresh trusted devices list
            this.getTrustedDevices()
        },
        showRemoveConfirmation(data) {
            this.selectedUserDeviceData = data
            const thisDevice = this.isThisDevice(data) ? '<span class="primary--text">(this device)</span>': ''
            this.dialogOkCancelMsg = `Remove <b>${data.deviceName} ${thisDevice}</b> from your trusted devices? Next time you sign in on this device, you’ll need your email, password, and OTP.<br><br><b>Do you want to continue?</b>`
            this.dialogOkCancel = true
        },
        async removeDeviceLogin() {
            try {
                await userDevicesApi.removeDevice(this.selectedUserDeviceData)
                await this.getTrustedDevices()
            }catch(errorMessage){
                this.error = true
                this.errorMessage = errorMessage
            }
        },
        async updateTwoFA() {
            try {  
                const data = {twoFA: this.twoFA}
                await userApi.updateTwoFA(data); 
                this.$store.commit('setTwoFA', this.twoFA)
            } catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        async updateNotifications() {
            try {  
                const data = {
                    appNotification: this.appNotification,
                    emailNotification: this.emailNotification
                }
                await userApi.updateNotifications(data); 
                this.$store.commit('setAppNotification', this.appNotification)
                this.$store.commit('setEmailNotification', this.emailNotification)
            } catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        updateHeaderImage(color){
            this.headerImage = color.toLowerCase() + '.jpg'
            this.$store.commit('setHeaderImage', this.headerImage)

            //update main header image
            this.$root.$emit('setHeaderTitles', {
                display: true,
                headerTitle: '',
                headerPageTitle: 'Settings',
                headerImgPath: this.headerImage
            })
        },
        async updateTheme(theme) {
            this.$root.$emit('updateTheme', theme)
            this.$store.commit('setTheme', theme)  
        },
        async checkPlatformAuthenticatorFeature() {
            this.deviceLoginSupported  = await isPlatformAuthenticatorAvailable()
        },
        async getTrustedDevices() {
            try {
                const response = await userDevicesApi.getTrusted()
                if (response && response.status == 200) {
                    this.trusted_devices = response.data.items
                }
            }catch(errorMessage){
                this.error = true
                this.errorMessage = errorMessage
            }
        },
        isThisDevice(data) {
            return data.deviceFingerprint == this.userAgent.fingerprint
        },
        canEnableThisDevice(data) {
            return this.deviceLoginSupported  &&
                this.isThisDevice(data) && 
                !data.isDeviceLoginEnabled
        },
        initialize() {
            this.headerImage = this.$store.state.headerImg.replace('.darken1','').replace('.darken2','').replace('.darken3','').replace('.darken4','')
            this.twoFA = this.$store.state.twoFA
            this.appNotification = this.$store.state.appNotification
            this.emailNotification = this.$store.state.emailNotification
        }
    },
    async created() {
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'My Settings'
        })

        this.initialize()

        this.userAgent = await getUAData()
        
        await this.checkPlatformAuthenticatorFeature()
        this.getTrustedDevices()
    },
    watch: {
        appearanceVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'mysettings-appearance', newVal)
        },
        securityVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'mysettings-security', newVal)
        },
        trustedDevicesVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'mysettings-trusted-devices', newVal)
        },
        notificationsVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'mysettings-notifications', newVal)
        },
    },
}
</script>
<style scoped>
.no-text-btn {
  width: 40px !important;
  min-width: 40px !important;
}
.section-layout {
    padding:0px 23px 0px 23px;
}
</style>